import { emptyActivity } from './activity.js';
import { artifactItemOf, createTranscript, IMPLICIT_TURN, mergeArtifact, reduceHeader, } from './model.js';
import { emptyProgress } from './progress.js';
export const MATERIAL_VERSION = 1;
/** Newest window cuts kept (older transcript pages fall back to the previous kept cut). */
export const MATERIAL_CUTS_KEPT = 256;
/**
 * An empty material adopts runId and agent (claude/codex) from its first event, exactly as an
 * empty transcript does; pass `runId` only when the agent is known to be the default.
 */
export function emptyMaterial(runId = '') {
    const t = createTranscript(runId);
    return {
        v: MATERIAL_VERSION,
        header: {
            progress: t.progress,
            activity: t.activity,
            runId: t.runId,
            agent: t.agent,
            session: t.session,
            status: t.status,
            todos: t.todos,
            usage: t.usage,
            ratelimits: t.ratelimits,
            queued: t.queued,
            lastSeq: t.lastSeq,
            lastTs: t.lastTs,
        },
        turnIds: [],
        asks: {},
        refs: {},
        artifacts: {},
        artifactItems: {},
        askText: {},
        open: {},
        cuts: [],
    };
}
const TERMINAL_TASK = new Set(['completed', 'failed', 'stopped']);
/** Pure incremental step (except the append-only `activity.files` set, as in the view). */
export function reduceMaterial(m, e) {
    const h = m.header;
    if (h.runId && e.runId === h.runId && e.seq <= h.lastSeq)
        return m;
    const next = { ...m, header: reduceHeader(h, e) };
    const last = m.turnIds.at(-1);
    // model.ts resolveTurnId without the refId lookup (only result-style events use it).
    const resolve = (turnId) => (e.parentId ? m.refs[e.parentId] : undefined) ?? turnId ?? last ?? IMPLICIT_TURN;
    const create = (id) => {
        if (next.turnIds.includes(id))
            return id;
        next.turnIds = [...next.turnIds, id];
        delete next.lastOutcome;
        if (!Object.keys(next.open).length) {
            next.cuts = [...next.cuts, [e.seq, next.turnIds.length]];
            if (next.cuts.length > MATERIAL_CUTS_KEPT)
                next.cuts = next.cuts.slice(-MATERIAL_CUTS_KEPT);
        }
        return id;
    };
    const setOpen = (id, kind) => {
        if (kind ? next.open[id] === kind : !(id in next.open))
            return;
        next.open = { ...next.open };
        if (kind)
            next.open[id] = kind;
        else
            delete next.open[id];
    };
    const setAsk = (id, text) => {
        next.asks = { ...next.asks };
        next.askText = { ...next.askText };
        if (text !== undefined) {
            next.asks[id] = (next.asks[id] ?? 0) + 1;
            next.askText[id] = text;
        }
        else {
            delete next.asks[id];
            delete next.askText[id];
        }
        setOpen(id, text !== undefined ? 'ask' : undefined);
    };
    switch (e.kind) {
        case 'turn.start':
        case 'asst.delta':
        case 'asst.block':
        case 'plan':
        case 'todo':
        case 'diff.turn':
            create(resolve(e.turnId));
            break;
        case 'tool.call': {
            const turn = create(resolve(e.turnId));
            if (e.toolKind === 'subagent' || e.background) {
                next.refs = { ...next.refs, [e.toolCallId]: turn };
                setOpen(e.toolCallId, e.background ? 'task' : 'agent');
            }
            break;
        }
        case 'tool.result': {
            // A background launch returns at once ("launched"); its cli.task carries the real end.
            const open = next.open[e.toolCallId];
            if (open === 'agent' || (open === 'task' && e.status !== 'ok'))
                setOpen(e.toolCallId, undefined);
            break;
        }
        case 'user.msg':
            if (e.queued && e.messageId)
                setOpen(e.messageId, 'queued');
            else
                create(resolve(e.turnId));
            break;
        case 'user.msg.status': {
            const queued = h.queued.some((q) => q.messageId === e.messageId);
            if (queued && (e.state === 'started' || e.state === 'cancelled'))
                setOpen(e.messageId, undefined);
            if (queued && e.state === 'started')
                create(resolve(e.turnId));
            break;
        }
        case 'turn.end': {
            const id = resolve(e.turnId);
            if (id === next.turnIds.at(-1))
                next.lastOutcome = e.outcome;
            break;
        }
        case 'question':
            create(resolve(e.turnId));
            setAsk(e.questionId, e.questions[0]?.question ?? 'Question');
            break;
        case 'question.answered':
            if (e.questionId in next.asks)
                setAsk(e.questionId, undefined);
            break;
        case 'approval':
            create(resolve(e.turnId));
            // A plan approval whose id is its tool call's id is folded away with the tool row.
            if (!(e.approvalKind === 'plan' && e.toolCallId === e.approvalId))
                setAsk(e.approvalId, String(e.title ?? e.command ?? 'Approval'));
            break;
        case 'approval.resolved':
            if (e.approvalId in next.asks)
                setAsk(e.approvalId, undefined);
            break;
        case 'artifact': {
            const item = artifactItemOf(e);
            const previous = m.artifactItems[e.artifactId];
            if (!(e.artifactId in m.artifacts)) {
                const turn = create(resolve(e.turnId));
                next.artifacts = { ...next.artifacts, [e.artifactId]: next.turnIds.indexOf(turn) + 1 };
            }
            next.artifactItems = {
                ...next.artifactItems,
                [e.artifactId]: previous ? mergeArtifact(previous, item) : item,
            };
            break;
        }
        case 'hook':
            if (e.turnId)
                create(resolve(e.turnId));
            break;
        case 'cli.task': {
            const tcid = e.toolCallId;
            const turnId = (tcid && m.refs[tcid]) || e.turnId || last;
            if (turnId)
                create((e.parentId ? m.refs[e.parentId] : undefined) ?? turnId);
            if (tcid)
                setOpen(tcid, TERMINAL_TASK.has(e.state) ? undefined : 'task');
            break;
        }
    }
    return next;
}
export function buildMaterial(events) {
    let m = emptyMaterial();
    for (const e of events)
        m = reduceMaterial(m, e);
    return m;
}
export function materialAwaiting(m) {
    let n = 0;
    for (const c of Object.values(m.asks))
        n += c;
    return n;
}
/** Same result as `runStateInput(view)` for the full transcript of the same events. */
export function materialInput(m) {
    const h = m.header;
    const turns = m.turnIds.length;
    return {
        progress: h.progress,
        status: h.status,
        turnOpen: h.status === 'running' ||
            h.status === 'compacting' ||
            (h.status !== 'error' && turns > 0 && !m.lastOutcome),
        awaiting: materialAwaiting(m),
        turns,
        ...(m.lastOutcome ? { lastOutcome: m.lastOutcome } : {}),
        ...(h.activity.lastEventAt ? { lastActivity: h.activity.lastEventAt } : {}),
    };
}
/**
 * A header-only view (no turns loaded yet): what list surfaces and the session header render
 * from a snapshot. `turnBase` carries the turn count so run-state derivations stay exact.
 */
export function materialView(m, connection = 'live') {
    return {
        ...createTranscript(m.header.runId, m.header.agent),
        ...m.header,
        connection,
        awaiting: materialAwaiting(m),
        turnBase: m.turnIds.length,
        material: m,
    };
}
// ---- wire form ------------------------------------------------------------------------------
/** Plain JSON (the activity file set becomes an array); undefined fields are dropped. */
export function materialToJson(m) {
    const h = m.header;
    return JSON.parse(JSON.stringify({
        ...m,
        header: { ...h, activity: { ...h.activity, files: [...h.activity.files] } },
    }));
}
const isObj = (x) => typeof x === 'object' && x !== null && !Array.isArray(x);
const isSeq = (x) => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0;
/**
 * Structural check of an authenticated snapshot's plaintext. Integrity comes from the AEAD
 * (only trusted endpoints hold the key); this rejects foreign versions and shapes that would
 * throw deep inside the reducers.
 */
export function materialFromJson(raw) {
    if (!isObj(raw) || raw.v !== MATERIAL_VERSION)
        throw new Error('unsupported run material');
    const h = raw.header;
    if (!isObj(h) ||
        typeof h.runId !== 'string' ||
        (h.agent !== 'claude' && h.agent !== 'codex') ||
        typeof h.status !== 'string' ||
        !isSeq(h.lastSeq) ||
        typeof h.lastTs !== 'number' ||
        !isObj(h.session) ||
        !Array.isArray(h.ratelimits) ||
        !Array.isArray(h.queued) ||
        !(h.todos === null || Array.isArray(h.todos)) ||
        !(h.usage === null || isObj(h.usage)) ||
        !isObj(h.progress) ||
        !Array.isArray(h.progress.checks) ||
        !Array.isArray(h.progress.commits) ||
        !isObj(h.activity) ||
        !Array.isArray(h.activity.steps) ||
        !Array.isArray(h.activity.files) ||
        !Array.isArray(raw.turnIds) ||
        !raw.turnIds.every((t) => typeof t === 'string') ||
        !isObj(raw.asks) ||
        !isObj(raw.refs) ||
        !isObj(raw.artifacts) ||
        !isObj(raw.artifactItems) ||
        !isObj(raw.askText) ||
        !isObj(raw.open) ||
        !Array.isArray(raw.cuts) ||
        !raw.cuts.every((c) => Array.isArray(c) && c.length === 2 && isSeq(c[0]) && isSeq(c[1])))
        throw new Error('invalid run material');
    const base = emptyMaterial();
    const progress = { ...emptyProgress(), ...h.progress };
    const activity = {
        ...emptyActivity(),
        ...h.activity,
        files: new Set(h.activity.files.map(String)),
    };
    return {
        ...base,
        ...raw,
        header: { ...base.header, ...h, progress, activity },
    };
}
//# sourceMappingURL=materialized.js.map